import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "@/components/Helmet";
import { Link, useLocation, useParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { LiveScanStatusPanel } from "@/components/live-scan/LiveScanStatusPanel";
import type { Finding, ReportSummary, ScanResult } from "@/pages/portal/autoscan/types/customer";
import {
  buildLiveScanPdfUrl,
  buildScanAuthHeaders,
  extractLiveLogText,
  extractFindings,
  extractScanResults,
  extractSummary,
  getPublicScanApiBase,
  normalizeDomain,
  parseScanStatus,
  resolveScanToken,
  scrubScanTokenFromUrl,
} from "@/lib/live-scan";

const PUBLIC_SCAN_API_BASE = getPublicScanApiBase();

const LiveScan = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const [scanStatus, setScanStatus] = useState<"idle" | "starting" | "running" | "completed" | "error">("idle");
  const [scanSummary, setScanSummary] = useState<ReportSummary | null>(null);
  const [, setScanFindings] = useState<Finding[]>([]);
  const [, setScanResults] = useState<ScanResult[]>([]);
  const [scanLog, setScanLog] = useState("");
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanTargetUrl, setScanTargetUrl] = useState<string | null>(null);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const [scanToken, setScanToken] = useState("");
  const logRef = useRef<HTMLPreElement | null>(null);
  const completionHandledRef = useRef<string | null>(null);
  const location = useLocation();

  const scanJobId = useMemo(() => (scanId ? String(scanId) : null), [scanId]);

  useEffect(() => {
    setScanLog("");
    setScanSummary(null);
    setScanFindings([]);
    setScanResults([]);
    setScanError(null);
    setScanTargetUrl(null);
    setPdfAvailable(false);
    setScanToken("");
    completionHandledRef.current = null;
    if (scanJobId) {
      setScanStatus("running");
    } else {
      setScanStatus("idle");
    }
  }, [scanJobId]);

  useEffect(() => {
    if (!scanJobId) return;
    const token = resolveScanToken(scanJobId, location.search, location.hash);
    setScanToken(token);
    scrubScanTokenFromUrl(location.search, location.hash);
  }, [location.hash, location.search, scanJobId]);

  useEffect(() => {
    const el = logRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [scanLog]);

  const finalizeScan = useCallback(
    async (payload: unknown) => {
      if (!scanJobId || completionHandledRef.current === scanJobId) return;
      completionHandledRef.current = scanJobId;
      const findings = extractFindings(payload);
      const summary = extractSummary(payload, findings);
      const results = extractScanResults(payload);
      setScanFindings(findings);
      setScanSummary(summary);
      setScanResults(results);
      setScanStatus("completed");
      setScanError(null);
      setPdfAvailable(true);
    },
    [scanJobId],
  );

  const fetchScanStatus = useCallback(async () => {
    if (!scanJobId) return;
    const res = await fetch(`${PUBLIC_SCAN_API_BASE}/scan/${scanJobId}`, {
      headers: buildScanAuthHeaders(scanToken),
    });
    if (!res.ok) {
      if (res.status === 403) {
        setScanStatus("error");
        setScanError("This scan link is invalid or has expired.");
        return;
      }
      if (res.status === 404) {
        setScanStatus("error");
        setScanError("Scan not found. Please double-check your link.");
        return;
      }
      throw new Error("Failed to fetch scan status");
    }
    const payload = await res.json();
    const job = (payload as { job?: { targetUrl?: string } }).job;
    if (job?.targetUrl) setScanTargetUrl(job.targetUrl);
    const liveLogText = extractLiveLogText(payload);
    if (liveLogText) setScanLog(liveLogText);
    const status = parseScanStatus(payload);
    setScanStatus(status);
    if (status === "completed") {
      await finalizeScan(payload);
    }
    if (status === "error") {
      setScanError("Scan failed. Please try again or contact support.");
    }
  }, [finalizeScan, scanJobId, scanToken]);

  useEffect(() => {
    if (!scanJobId) return;
    fetchScanStatus().catch(() => {});
  }, [fetchScanStatus, scanJobId]);

  useEffect(() => {
    if (!scanJobId || scanStatus === "completed" || scanStatus === "error") return;
    const interval = setInterval(() => {
      fetchScanStatus().catch(() => {});
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchScanStatus, scanJobId, scanStatus]);

  const handleDownloadPdf = () => {
    if (!scanJobId) return;
    const domain = normalizeDomain(scanTargetUrl || scanJobId);
    window.open(buildLiveScanPdfUrl(scanJobId, domain, scanToken), "_blank");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Live Scan Status | ShadowScan</title>
        <meta
          name="description"
          content="Track your ShadowScan live scan progress and download your PDF report."
        />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
        <meta httpEquiv="Cache-Control" content="no-store" />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24">
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// LIVE_SCAN</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Live Scan Status
              </h1>
              <p className="text-sm text-muted-foreground">
                Track progress and download your report when the scan finishes.
              </p>
              <p className="mt-3 text-xs text-muted-foreground">
                Authorised testing only. This live scan is a non-intrusive assessment. See{" "}
                <Link className="text-primary underline hover:no-underline" to="/terms">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link className="text-primary underline hover:no-underline" to="/privacy">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto cyber-card rounded-xl p-6 border-primary/30">
              {scanStatus === "idle" ? (
                <p className="text-sm text-muted-foreground text-center">
                  Missing scan ID. Please use the link from your email.
                </p>
              ) : (
                <LiveScanStatusPanel
                  status={scanStatus}
                  scanLog={scanLog}
                  scanSummary={scanSummary}
                  scanError={scanError}
                  pdfAvailable={pdfAvailable}
                  onDownloadPdf={handleDownloadPdf}
                  logRef={logRef}
                />
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LiveScan;
